import { useEffect, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { coinValue } from "../../sim/money";
import { authHeaders } from "../../sim/save";
import { authEnabled } from "../AuthGate";

/**
 * WHO IS LOOKING AT THE HOME PAGE — so it can greet a first-time visitor and a returning gardener differently.
 *
 * A garden on THIS DEVICE is found in localStorage (the game keeps its save under `shelf-garden.save.<owner>`,
 * sim/save.ts), with no network at all — so even a signed-out guest who played yesterday gets "Welcome back".
 * A SIGNED-IN player's cloud save is asked for too (`/api/save`, the same read the Profile page makes), and the
 * newer of the two wins. Both are best-effort: anything unreadable simply means "no garden found yet", and the
 * page falls back to its first-visit form rather than showing an error.
 */

export interface GardenSummary {
  coins: number;
  petals: number;
  /** pots with something growing in them */
  growing: number;
  /** when the save was last written (ms), or 0 if unknown */
  savedAt: number;
  /** where it came from: this browser only, or the account */
  source: "device" | "cloud";
}

function summarise(raw: unknown, source: GardenSummary["source"]): GardenSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const xp = typeof o.xp === "number" ? o.xp : 0;
  const coins = typeof o.coins === "number" ? coinValue(o.coins) : 0;
  const petals = typeof o.petals === "number" && o.petals >= 0 ? Math.floor(o.petals) : 0;
  const plantings = o.plantings && typeof o.plantings === "object" ? Object.keys(o.plantings as object).length : 0;
  const savedAt = typeof o.savedAt === "number" && Number.isFinite(o.savedAt) ? o.savedAt : 0;
  // a brand-new, never-played save is not a "garden" worth welcoming back to
  if (xp <= 0 && plantings === 0 && savedAt === 0) return null;
  // (no level: the game has THREE levelling tracks — gardening, fishing, naturalist — and one number would mislead)
  return { coins, petals, growing: plantings, savedAt, source };
}

/** The most recently saved garden in this browser, if any. */
export function deviceGarden(): GardenSummary | null {
  let best: GardenSummary | null = null;
  try {
    for (let k = 0; k < localStorage.length; k++) {
      const key = localStorage.key(k);
      if (!key || !key.startsWith("shelf-garden.save.")) continue;
      const s = summarise(JSON.parse(localStorage.getItem(key) || "null"), "device");
      if (s && (!best || s.savedAt > best.savedAt)) best = s;
    }
  } catch { /* private mode / corrupt entry — treat as nothing found */ }
  return best;
}

/** The signed-in player's cloud garden, or null (none yet, or unreachable). */
export async function cloudGarden(): Promise<GardenSummary | null> {
  try {
    const res = await fetch("/api/save", { headers: await authHeaders() });
    if (!res.ok) return null;
    return summarise(await res.json(), "cloud");
  } catch { return null; }
}

/** "3 minutes ago" / "2 days ago" — for "last played". */
export function agoText(ms: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60); if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60); if (h < 48) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24); return `${d} day${d === 1 ? "" : "s"} ago`;
}

export type Who = { signedIn: boolean; name?: string };

/** Signed-in state + a friendly name, when this build has auth. (`authEnabled` is a build-time constant, so the
 *  hooks run the same on every render.) */
export function useWho(): Who {
  if (!authEnabled) return { signedIn: false };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isSignedIn } = useAuth();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { user } = useUser();
  return { signedIn: !!isSignedIn, name: user?.username || user?.firstName || undefined };
}

/** One cloud read per page load, SHARED by the nav's wallet and the home page's welcome — not one each. */
let cloudOnce: Promise<GardenSummary | null> | null = null;

/** The garden this visitor already has: the newer of this device's save and (signed in) the cloud's. */
export function useGardenSummary(signedIn: boolean): { garden: GardenSummary | null; loading: boolean } {
  const [device] = useState(() => deviceGarden());
  const [cloud, setCloud] = useState<GardenSummary | null>(null);
  const [loading, setLoading] = useState(signedIn);
  useEffect(() => {
    if (!signedIn) { cloudOnce = null; setCloud(null); setLoading(false); return; }   // signed out: forget that account's purse
    let alive = true;
    setLoading(true);
    (cloudOnce ??= cloudGarden()).then((g) => { if (alive) { setCloud(g); setLoading(false); } });
    return () => { alive = false; };
  }, [signedIn]);
  const garden = cloud && device ? (cloud.savedAt >= device.savedAt ? cloud : device) : (cloud ?? device);
  return { garden, loading };
}
